import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, Scale } from "lucide-react";

/**
 * Terms of Service — public route (no auth required) so Google Cloud
 * Console's OAuth Consent Screen can crawl it for verification.
 * URL must match the value entered in the consent screen exactly: /terms.
 *
 * The component renders one of two language-specific subtrees based on
 * the active i18n language. Hebrew is preserved verbatim from the
 * pre-i18n baseline.
 *
 * Last updated: May 18, 2026 / 1 Sivan 5786
 */
export default function Terms() {
  const { i18n } = useTranslation();
  if (i18n.language === "en") return <TermsEn />;
  return <TermsHe />;
}

function TermsHe() {
  return (
    <div
      className="mx-auto max-w-3xl px-4 sm:px-6 py-12 md:py-20"
      data-testid="terms-page"
      dir="rtl"
    >
      <header className="mb-10 text-center">
        <div className="flex justify-center mb-4">
          <div className="h-12 w-12 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center">
            <Scale className="h-5 w-5 text-gold" strokeWidth={1.5} />
          </div>
        </div>
        <h1 className="font-heading text-4xl md:text-5xl">תנאי שימוש</h1>
        <p className="font-latin italic text-sm text-gold/80 mt-2">
          תוריא · Torahya
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          תאריך עדכון אחרון: 18 במאי 2026 · א' סיון תשפ"ו
        </p>
        <div className="ornamental-divider mt-4">✦</div>
      </header>

      <div className="space-y-7 text-foreground leading-relaxed text-[15px]">
        <Section title="ברוכים הבאים לתוריא">
          <p>
            תוריא היא אפליקציה ללימוד תורה, מנוהלת על ידי צוות תוריא. תוריא
            בתהליך רישום כארגון ללא מטרות רווח (Non-Profit,
            501(c)(3) Pending) בארצות הברית.
          </p>
          <p className="mt-2">
            בשימוש באפליקציה — אתה מסכים לתנאים הבאים.
          </p>
        </Section>

        <Section title="השימוש המותר">
          <p>תוריא נועדה ללימוד אישי של תורה. השימוש המותר כולל:</p>
          <ul className="list-disc pr-6 mt-2 space-y-1">
            <li>קריאת טקסטים תורניים</li>
            <li>בניית שיעורים אישיים</li>
            <li>שמירת רשומות והערות</li>
            <li>שיתוף קישור הפניה לחברים</li>
            <li>תרומה למטרות תוריא</li>
          </ul>

          <p className="font-heading text-foreground mt-4">השימוש האסור:</p>
          <ul className="list-disc pr-6 mt-1 space-y-1">
            <li>פעילות בלתי חוקית</li>
            <li>העלאת תוכן פוגעני, שקרי, או מטעה</li>
            <li>ניסיון לפרוץ למערכת</li>
            <li>העתקת תוכן ומכירתו</li>
            <li>איסוף מידע על משתמשים אחרים</li>
            <li>שימוש לרעה במשאבי המערכת (ספאם, bots)</li>
          </ul>
        </Section>

        <Section title="תוכן המקורות">
          <ul className="list-disc pr-6 space-y-1">
            <li>
              <strong>טקסטי התנ"ך, משנה, ופרשנים</strong> — באדיבות Sefaria
              (תחת רישיון Creative Commons — ראה sefaria.org)
            </li>
            <li><strong>נתוני לוח שנה וזמני הלכה</strong> — HebCal</li>
            <li>
              <strong>תוכן זה לא בבעלות תוריא</strong> — אנחנו מתווכים שמסדרים
              את התוכן בצורה נגישה
            </li>
          </ul>
        </Section>

        <Section title="חשבון משתמש">
          <ul className="list-disc pr-6 space-y-1">
            <li>אתה אחראי לסיסמא שלך</li>
            <li>חשבון אחד למשתמש</li>
            <li>מינימום גיל: 13</li>
            <li>אסור לשתף חשבון עם אחרים</li>
            <li>אנחנו זכאים לסגור חשבון שמפר תנאים אלה</li>
          </ul>
        </Section>

        <Section title="תרומות">
          <p>
            תוריא היא <strong>non-profit</strong> הפועלת על תרומות בלבד.
          </p>

          <p className="font-heading text-foreground mt-4">כללי תרומות:</p>
          <ul className="list-disc pr-6 mt-1 space-y-1">
            <li>כל התרומות הן <strong>וולונטריות</strong></li>
            <li>תרומות <strong>לא חוזרות</strong> (אלא במקרים חריגים — צור איתנו קשר תוך 14 ימים)</li>
            <li>כספי התרומות משמשים לתחזוקה ופיתוח של תוריא</li>
          </ul>
          <Notice>
            <strong>תוריא בתהליך רישום כ-501(c)(3) בארה"ב</strong>. כשהאישור
            יושלם, תרומות תהיינה ניתנות לניכוי מס בארה"ב. עד אז,{" "}
            <strong>התעודה המוצגת היא לציון אישי בלבד</strong>, לא תעודת מס רשמית.
          </Notice>

          <p className="font-heading text-foreground mt-4">תרומה חודשית:</p>
          <ul className="list-disc pr-6 mt-1 space-y-1">
            <li>תרומה חודשית מתחדשת אוטומטית בכל חודש</li>
            <li>ניתן לבטל בכל עת דרך הגדרות החשבון</li>
            <li>ביטול תקף ממחזור החיוב הבא</li>
          </ul>
        </Section>

        <Section title="אחריות וזמני שבת">
          <Notice>
            <p className="font-heading">חשוב במיוחד:</p>
            <ul className="list-disc pr-6 mt-1 space-y-1">
              <li>האפליקציה מסופקת <strong>"AS IS"</strong> ("כמות שהיא")</li>
              <li>אנחנו לא מתחייבים שהיא תהיה ללא תקלות</li>
              <li>
                <strong>המידע ההלכתי הוא לעיון בלבד, לא תחליף לרב מוסמך</strong>
              </li>
              <li>
                <strong>זמני שבת וחג</strong> — חובה לאמת מול לוח שנה רשמי לפני
                שימוש הלכתי. אנחנו לא אחראים לטעויות או לחילול שבת/חג בעקבות
                שימוש בנתונים שלנו.
              </li>
            </ul>
          </Notice>
        </Section>

        <Section title="קניין רוחני">
          <ul className="list-disc pr-6 space-y-1">
            <li>העיצוב, הקוד והפיצ'רים של תוריא — בבעלות צוות תוריא</li>
            <li>תוכן תורני (פסוקים, פרשנים, משנה) — לא בבעלותנו, ראה רישיון Sefaria</li>
            <li>הרשומות, ההערות, והשיעורים שאתה יוצר — בבעלותך המלאה</li>
          </ul>
        </Section>

        <Section title="שינויים בשירות">
          <p>אנחנו רשאים בכל עת:</p>
          <ul className="list-disc pr-6 mt-2 space-y-1">
            <li>לשנות פיצ'רים</li>
            <li>להוסיף פיצ'רים חדשים</li>
            <li>להסיר פיצ'רים קיימים</li>
            <li>לעדכן את התנאים</li>
          </ul>
          <p className="mt-2">שינויים משמעותיים — נודיע מראש.</p>
        </Section>

        <Section title="הגבלת אחריות">
          <p>
            תוריא, מנהליה, ועובדיה לא יהיו אחראים לנזקים ישירים, עקיפים, או כל
            נזק אחר הנובע משימוש באפליקציה, כולל אך לא רק:
          </p>
          <ul className="list-disc pr-6 mt-2 space-y-1">
            <li>אובדן נתונים</li>
            <li>אובדן רווחים</li>
            <li>חוסר זמינות זמני</li>
            <li>אי-דיוקים בנתונים תורניים או הלכתיים</li>
          </ul>
          <Notice>
            הגבלת אחריות זו אינה חלה במקרים של רשלנות חמורה או הפרת חוק.
          </Notice>
        </Section>

        <Section title="סיום השימוש">
          <ul className="list-disc pr-6 space-y-1">
            <li><strong>אתה</strong> יכול לסגור את חשבונך בכל עת דרך הגדרות החשבון</li>
            <li><strong>אנחנו</strong> זכאים לסגור חשבון שמפר תנאים אלה, עם הודעה מראש כשאפשר</li>
            <li>מחיקת חשבון תמחק את כל הנתונים שלך תוך 30 ימים</li>
          </ul>
        </Section>

        <Section title="חוק החל וסמכות שיפוט">
          תנאים אלה כפופים לחוקי ארצות הברית. כל מחלוקת תיפתר בערכאות המוסמכות
          בארה"ב.
        </Section>

        <Section title="ניתוק תנאים">
          אם סעיף כלשהו ייחשב כבלתי תקף — שאר התנאים יישארו בתוקף.
        </Section>

        <Section title="ההסכם המלא">
          <p>
            תנאים אלה +{" "}
            <Link
              to="/privacy"
              className="text-gold underline"
              data-testid="terms-link-privacy"
            >
              מדיניות הפרטיות
            </Link>{" "}
            מהווים את ההסכם המלא בינך לבין תוריא.
          </p>
        </Section>

        <Section title="יצירת קשר">
          <p>
            לכל שאלה:{" "}
            <a
              href="mailto:7torahya@gmail.com"
              className="text-gold underline"
              data-testid="terms-contact-email"
            >
              7torahya@gmail.com
            </a>
          </p>
        </Section>
      </div>

      <div className="mt-12 pt-6 border-t border-border/40 text-center">
        <Link
          to="/"
          data-testid="terms-back-home"
          className="inline-flex items-center gap-2 text-sm font-heading text-gold hover:text-gold/80 transition-colors"
        >
          <ArrowRight className="h-4 w-4 rtl:rotate-180" strokeWidth={1.5} />
          חזרה לדף הבית
        </Link>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-xl md:text-2xl text-[#1E3A5F] dark:text-gold mb-2">
        {title}
      </h2>
      <div className="text-foreground/85 space-y-2">{children}</div>
    </section>
  );
}

function Notice({ children }) {
  return (
    <div className="my-3 rounded-sm border-r-4 border-gold/70 bg-gold/5 px-4 py-3 text-sm text-foreground/90">
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- //
// English version — independent LTR subtree. Mirrors the Hebrew copy
// section-for-section so an English-mode user reads the same scope of
// terms.
// ---------------------------------------------------------------- //
function TermsEn() {
  return (
    <div
      className="mx-auto max-w-3xl px-4 sm:px-6 py-12 md:py-20"
      data-testid="terms-page"
      dir="ltr"
    >
      <header className="mb-10 text-center">
        <div className="flex justify-center mb-4">
          <div className="h-12 w-12 rounded-full bg-gold/10 border border-gold/40 flex items-center justify-center">
            <Scale className="h-5 w-5 text-gold" strokeWidth={1.5} />
          </div>
        </div>
        <h1 className="font-heading text-4xl md:text-5xl">Terms of Service</h1>
        <p className="font-latin italic text-sm text-gold/80 mt-2">
          Torahya · תוריא
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          Last updated: May 18, 2026 · 1 Sivan 5786
        </p>
        <div className="ornamental-divider mt-4">✦</div>
      </header>

      <div className="space-y-7 text-foreground leading-relaxed text-[15px]">
        <SectionEn title="Welcome to Torahya">
          <p>
            Torahya is a Torah-learning application operated by the Torahya
            team. Torahya is in the process of registering as a non-profit
            organization (Non-Profit, 501(c)(3) Pending) in the United States.
          </p>
          <p className="mt-2">
            By using the application — you agree to the terms below.
          </p>
        </SectionEn>

        <SectionEn title="Permitted use">
          <p>Torahya is intended for personal Torah study. Permitted use includes:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Reading Torah texts</li>
            <li>Building personal lessons</li>
            <li>Saving entries and notes</li>
            <li>Sharing an invite link with friends</li>
            <li>Donating to Torahya causes</li>
          </ul>

          <p className="font-heading text-foreground mt-4">Prohibited use:</p>
          <ul className="list-disc pl-6 mt-1 space-y-1">
            <li>Unlawful activity</li>
            <li>Uploading offensive, false, or misleading content</li>
            <li>Attempts to breach the system</li>
            <li>Copying content and reselling it</li>
            <li>Harvesting data about other users</li>
            <li>Abuse of system resources (spam, bots)</li>
          </ul>
        </SectionEn>

        <SectionEn title="Source content">
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Tanakh, Mishnah, and commentary texts</strong> — courtesy of
              Sefaria (under a Creative Commons license — see sefaria.org)
            </li>
            <li><strong>Calendar data and halachic times</strong> — HebCal</li>
            <li>
              <strong>This content is not owned by Torahya</strong> — we are a
              mediator that arranges the content in an accessible way.
            </li>
          </ul>
        </SectionEn>

        <SectionEn title="User account">
          <ul className="list-disc pl-6 space-y-1">
            <li>You are responsible for your password</li>
            <li>One account per user</li>
            <li>Minimum age: 13</li>
            <li>Do not share an account with others</li>
            <li>We may close an account that violates these terms</li>
          </ul>
        </SectionEn>

        <SectionEn title="Donations">
          <p>
            Torahya is a <strong>non-profit</strong> that operates on donations only.
          </p>

          <p className="font-heading text-foreground mt-4">Donation rules:</p>
          <ul className="list-disc pl-6 mt-1 space-y-1">
            <li>All donations are <strong>voluntary</strong></li>
            <li>Donations are <strong>non-refundable</strong> (except in exceptional cases — contact us within 14 days)</li>
            <li>Donation funds are used for the maintenance and development of Torahya</li>
          </ul>
          <NoticeEn>
            <strong>Torahya is in the process of registering as a 501(c)(3) in the US.</strong>{" "}
            When the approval is complete, donations will be tax-deductible in the US.
            Until then, <strong>the certificate displayed is for personal record only</strong>,
            not an official tax receipt.
          </NoticeEn>

          <p className="font-heading text-foreground mt-4">Monthly donations:</p>
          <ul className="list-disc pl-6 mt-1 space-y-1">
            <li>A monthly donation renews automatically each month</li>
            <li>You can cancel at any time from your account settings</li>
            <li>Cancellation takes effect from the next billing cycle</li>
          </ul>
        </SectionEn>

        <SectionEn title="Liability and Shabbat times">
          <NoticeEn>
            <p className="font-heading">Especially important:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>The application is provided <strong>"AS IS"</strong></li>
              <li>We do not guarantee it will be error-free</li>
              <li>
                <strong>Halachic information is for reference only — not a substitute for a qualified rabbi</strong>
              </li>
              <li>
                <strong>Shabbat and Yom Tov times</strong> — you are required to verify them
                against an official calendar before any halachic use. We are not responsible
                for errors or chillul Shabbat / Yom Tov resulting from use of our data.
              </li>
            </ul>
          </NoticeEn>
        </SectionEn>

        <SectionEn title="Intellectual property">
          <ul className="list-disc pl-6 space-y-1">
            <li>The design, code, and features of Torahya — owned by the Torahya team</li>
            <li>Torah content (verses, commentaries, Mishnah) — not owned by us; see Sefaria's license</li>
            <li>The entries, notes, and lessons you create — owned entirely by you</li>
          </ul>
        </SectionEn>

        <SectionEn title="Changes to the service">
          <p>We may at any time:</p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Change features</li>
            <li>Add new features</li>
            <li>Remove existing features</li>
            <li>Update the terms</li>
          </ul>
          <p className="mt-2">Material changes — we will give advance notice.</p>
        </SectionEn>

        <SectionEn title="Limitation of liability">
          <p>
            Torahya, its operators, and its employees will not be liable for direct,
            indirect, or any other damages arising from the use of the application,
            including but not limited to:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>Loss of data</li>
            <li>Loss of profits</li>
            <li>Temporary unavailability</li>
            <li>Inaccuracies in Torah or halachic data</li>
          </ul>
          <NoticeEn>
            This limitation does not apply in cases of gross negligence or
            violation of law.
          </NoticeEn>
        </SectionEn>

        <SectionEn title="Termination">
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>You</strong> can close your account at any time from account settings</li>
            <li><strong>We</strong> may close an account that violates these terms, with prior notice when possible</li>
            <li>Account deletion will erase all your data within 30 days</li>
          </ul>
        </SectionEn>

        <SectionEn title="Governing law and jurisdiction">
          These terms are governed by the laws of the United States. Any dispute
          will be resolved in the appropriate courts of the US.
        </SectionEn>

        <SectionEn title="Severability">
          If any clause is held invalid — the remaining terms will remain in effect.
        </SectionEn>

        <SectionEn title="Entire agreement">
          <p>
            These terms +{" "}
            <Link
              to="/privacy"
              className="text-gold underline"
              data-testid="terms-link-privacy"
            >
              the privacy policy
            </Link>{" "}
            constitute the entire agreement between you and Torahya.
          </p>
        </SectionEn>

        <SectionEn title="Contact">
          <p>
            For any question:{" "}
            <a
              href="mailto:7torahya@gmail.com"
              className="text-gold underline"
              data-testid="terms-contact-email"
            >
              7torahya@gmail.com
            </a>
          </p>
        </SectionEn>
      </div>

      <div className="mt-12 pt-6 border-t border-border/40 text-center">
        <Link
          to="/"
          data-testid="terms-back-home"
          className="inline-flex items-center gap-2 text-sm font-heading text-gold hover:text-gold/80 transition-colors"
        >
          <ArrowRight className="h-4 w-4 rotate-180" strokeWidth={1.5} />
          Back to home
        </Link>
      </div>
    </div>
  );
}

function SectionEn({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-xl md:text-2xl text-[#1E3A5F] dark:text-gold mb-2">
        {title}
      </h2>
      <div className="text-foreground/85 space-y-2">{children}</div>
    </section>
  );
}

function NoticeEn({ children }) {
  return (
    <div className="my-3 rounded-sm border-l-4 border-gold/70 bg-gold/5 px-4 py-3 text-sm text-foreground/90">
      {children}
    </div>
  );
}
